/**
 * Media URL resolution utility.
 *
 * The media pipeline stores bare paths in the database (e.g. `/orgId/assetId/master.m3u8`).
 * The VITE_MEDIA_HOST environment variable holds the current CDN / R2 hostname so that
 * it can be changed at any time without touching the database or re-processing assets.
 *
 * Transition safety: if a value is already a full URL (legacy rows written before this
 * change), it is returned unchanged so the app keeps working during rollout.
 *
 * Cache-busting for the HLS flag is applied at the hls.js request level (see the
 * loader in VideoPlayer.tsx), not here — so it reaches segments, which a query on
 * the master URL cannot (hls.js drops the master's query when resolving children).
 */
export function resolveMediaUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  // Already a full URL — legacy row or external URL, pass through unchanged
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const host = import.meta.env.VITE_MEDIA_HOST ?? '';
  return `${host}${path}`;
}

// ── Video thumbnails ──────────────────────────────────────────────────────────
//
// After transcoding, media.thumbnail_url holds a JSON array of VideoThumbnail
// objects (NOT a single image URL). Each entry has sm/md/lg bare paths that
// need resolveMediaUrl() applied before use.
//
// Format written by the pipeline:
//   [{ index, timestamp, sm: "/org/asset/thumbnails/0_sm.jpg", md: "...", lg: "..." }]

export interface VideoThumbnail {
  index: number;
  timestamp: number; // seconds from start
  sm: string;        // ~120×68 resolved URL
  md: string;        // ~240×135 resolved URL
  lg: string;        // ~480×270 resolved URL
}

/**
 * Parse media.thumbnail_url (a JSON array string) and resolve each sm/md/lg
 * path to a full URL using VITE_MEDIA_HOST.
 *
 * Returns [] if the value is null, not valid JSON, or not an array.
 */
export function resolveVideoThumbnails(raw: string | null | undefined): VideoThumbnail[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as Array<{ index: number; timestamp: number; sm: string; md: string; lg: string }>;
    if (!Array.isArray(parsed)) return [];
    return parsed.map(t => ({
      index: t.index,
      timestamp: t.timestamp,
      sm: resolveMediaUrl(t.sm) ?? t.sm,
      md: resolveMediaUrl(t.md) ?? t.md,
      lg: resolveMediaUrl(t.lg) ?? t.lg,
    }));
  } catch {
    return [];
  }
}
